import React, { useCallback, useEffect, useRef, useState } from "react";
import CustomTooltip from "../../../../components/common/CustomTooltip";
import TruncatedText from "../../../../components/common/TruncatedText";
import { Group } from "./TimelineLeftPanel";
import Portal from "../../../../components/Portal";

interface ResourceRowProps {
  group: Group;
  skills: string[];
  leftboxWidth: number;
  boxheight: number;
  getSkillColor: (index: number) => string;
  displayStoryPoints: number;
  displayOriginalEstimate?: number;
  displayTaskCount: number;
  tooltipContent: React.ReactNode;
  showMetrics?: boolean;
  isOptionalResource?: boolean;
  capacityPlanningLoading?: boolean;
  searchType?: string;
  selectedTeams?: string[];
}

const ResourceRow: React.FC<ResourceRowProps> = ({
  group,
  skills,
  leftboxWidth,
  boxheight,
  getSkillColor,
  displayStoryPoints,
  displayOriginalEstimate = 0,
  displayTaskCount,
  tooltipContent,
  showMetrics,
  isOptionalResource = false,
  capacityPlanningLoading = false,
  searchType = '',
  selectedTeams = [],
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ left: 0, top: 0 });
  const [arrowPos, setArrowPos] = useState({ top: 0, side: 'left' as 'left' | 'right' });
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const showTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);

  const TOOLTIP_WIDTH = 340;
  const VIEWPORT_MARGIN = 12;
  const ARROW_SIZE = 12;

  const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

  const calculateTooltipPosition = useCallback(() => {
    if (!itemRef.current) return;
    const rect = itemRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Always position to the right of the left panel
    const rightCandidate = rect.right + VIEWPORT_MARGIN;
    const left = clamp(rightCandidate, VIEWPORT_MARGIN, vw - TOOLTIP_WIDTH - VIEWPORT_MARGIN);

    const provisionalTop = rect.top + rect.height / 2;
    setTooltipPos({ left, top: provisionalTop });

    requestAnimationFrame(() => {
      const el = tooltipRef.current;
      if (!el) return;
      const th = el.offsetHeight || 200;
      const centeredTop = rect.top + rect.height / 2 - th / 2;
      const top = clamp(centeredTop, VIEWPORT_MARGIN, vh - th - VIEWPORT_MARGIN);
      setTooltipPos(prev => ({ ...prev, top }));

      const itemCenterY = rect.top + rect.height / 2;
      const arrowTop = clamp(itemCenterY - top - ARROW_SIZE / 2, 8, th - ARROW_SIZE - 8);
      setArrowPos({ top: arrowTop, side: 'left' });
    });
  }, []);

  const handleMouseEnter = () => {
    if (!showMetrics) return;
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
    }
    showTimerRef.current = setTimeout(() => {
      setShowTooltip(true);
      setTimeout(calculateTooltipPosition, 0);
      showTimerRef.current = null;
    }, 250);
  };

  const handleMouseLeave = () => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
    hideTimerRef.current = setTimeout(() => {
      if (!isTooltipHovered) {
        setShowTooltip(false);
      }
      hideTimerRef.current = null;
    }, 200);
  };

  useEffect(() => {
    if (!showTooltip) return;
    const onResize = () => calculateTooltipPosition();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [showTooltip, calculateTooltipPosition]);

  useEffect(() => {
    return () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  return (
    <>
      <div
        ref={itemRef}
        key={group.id}
        style={{
          width: leftboxWidth + 1,
          height: boxheight,
          boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
        }}
        className="relative border-r-2 shadow-xl dark:border-[#374151] border-b border-gray-300 dark:border-gray-600/50 transition-colors duration-150 bg-white dark:bg-secondary-dark hover:bg-hover-light dark:hover:bg-hover-dark"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className="cursor-pointer items-center h-full min-w-0"
          style={{ padding: 8 }}
        >
          {/* Row 1: Resource name + Work Items count */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              {/* Show circle-alert warning when: optional resource + sprint mode + not loading + exactly 1 team selected */}
              {isOptionalResource && searchType === 'sprint' && !capacityPlanningLoading && selectedTeams && selectedTeams.length === 1 && (
                <div className="flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4 text-yellow-500 flex-shrink-0"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-label="Resource assigned work items but not in team"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" x2="12" y1="8" y2="12"></line>
                    <line x1="12" x2="12.01" y1="16" y2="16"></line>
                  </svg>
                </div>
              )}
              {showMetrics ? (
                <div className="text-gray-700 dark:text-white text-sm font-medium leading-normal flex-1 whitespace-nowrap text-ellipsis overflow-hidden block min-w-0">
                  {group.title}
                </div>
              ) : (
                <TruncatedText
                  text={group.title}
                  className="text-gray-700 dark:text-white text-sm font-medium leading-normal flex-1 whitespace-nowrap text-ellipsis overflow-hidden block min-w-0"
                  placement="top-start"
                />
              )}
            </div>
            {/* Work Items count - plain text on right */}
            {showMetrics && (
              <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap flex-shrink-0">
                {displayTaskCount} Work Items
              </span>
            )}
          </div>

          {/* Row 2: Skills pills + SP/OE badge */}
          <div className="flex items-center justify-between gap-2 mt-1">
            {/* Skills rendered as individual pills */}
            {skills.length === 0 ? (
              <div className="whitespace-nowrap text-ellipsis overflow-hidden text-gray-400 dark:text-gray-500 text-xs font-normal leading-[16px] flex-1 italic">
                No skills
              </div>
            ) : (
              <div className="flex flex-nowrap gap-1.5 flex-1 max-w-full overflow-hidden">
                {/* Always show the first skill */}
                {skills.slice(0, 1).map((skill, idx) => (
                  <CustomTooltip
                    key={idx}
                    title={skill.trim()}
                    placement="top"
                  >
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border whitespace-nowrap ${getSkillColor(idx)}`}
                    >
                      {skill.trim()}
                    </span>
                  </CustomTooltip>
                ))}

                {/* Remaining skills tooltip */}
                {skills.length > 1 && (
                  <CustomTooltip
                    title={
                      <div className="flex flex-col gap-0.5">
                        {skills.slice(1).map((skill, idx) => (
                          <div key={idx} className="text-xs">
                            {skill.trim()}
                          </div>
                        ))}
                      </div>
                    }
                    placement="right"
                  >
                    <span
                      className="skills-toggle inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 whitespace-nowrap"
                    >
                      +{skills.length - 1}
                    </span>
                  </CustomTooltip>
                )}
              </div>
            )}

            {/* SP & OE Badge - on right side of row 2 */}
            {showMetrics && (
              <div
                className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-200 shadow-sm hover:shadow flex-shrink-0 cursor-pointer"
                title="Hover to see story points breakdown"
              >
                <span className="min-w-[14px] text-center text-xs font-semibold text-purple-700 dark:text-purple-300">
                  {displayStoryPoints.toFixed(0)}
                </span>
                <span className="text-[9px] font-medium text-purple-600 dark:text-purple-400 uppercase tracking-wide">
                  SP
                </span>
                <div className="w-px h-2.5 bg-gray-300 dark:bg-gray-600 mx-0.5"></div>
                <span className={`min-w-[14px] text-center text-xs font-semibold ${displayOriginalEstimate > 0 ? 'text-teal-700 dark:text-teal-300' : 'text-gray-400 dark:text-gray-500'} transition-colors`}>
                  {displayOriginalEstimate.toFixed(0)}
                </span>
                <span className={`text-[9px] font-medium ${displayOriginalEstimate > 0 ? 'text-teal-600 dark:text-teal-400' : 'text-gray-400 dark:text-gray-500'} uppercase tracking-wide`}>
                  OE
                </span>
                <svg
                  className="w-2.5 h-2.5 text-gray-400 dark:text-gray-500 hover:text-purple-600 dark:hover:text-purple-400 transition-all duration-200 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            )}
          </div>
        </div>
      </div>

      {showMetrics && showTooltip && (
        <Portal>
          <div
            ref={tooltipRef}
            className="z-[9999] w-[340px] shadow-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-lg fixed"
            style={{
              left: tooltipPos.left,
              top: tooltipPos.top,
            }}
            onMouseEnter={() => {
              setIsTooltipHovered(true);
              if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
              }
            }}
            onMouseLeave={() => {
              setIsTooltipHovered(false);
              if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
              }
              hideTimerRef.current = setTimeout(() => {
                setShowTooltip(false);
                hideTimerRef.current = null;
              }, 150);
            }}
          >
            <div
              className="pointer-events-none absolute"
              style={{
                top: arrowPos.top,
                left: arrowPos.side === 'left' ? -6 : undefined,
                right: arrowPos.side === 'right' ? -6 : undefined,
              }}
            >
              <div className="w-3 h-3 rotate-45 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700" />
            </div>
            {tooltipContent}
          </div>
        </Portal>
      )}
    </>
  );
};

export default ResourceRow;